import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
  Alert,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { api } from '../../../../services/api';
import { DetailSection } from '../../TripDetailPanel.styles';
import {AppDispatch, RootState} from "../../../../store/store";
import { useDispatch, useSelector } from "react-redux";
import { checkCarAvailability, clearAvailability } from '../../../../store/slices/carAvailability';
import CarAvailabilityDialog from './CarAvailabilityDialog';

interface Car {
  id: number;
  make?: string;
  model?: string;
  carType?: string | {
    id?: number;
    type?: string;
    makeCompany?: string;
    model?: string;
    capacity?: number;
    fuelType?: string;
    mileage?: number;
    bootSpace?: number;
  };
  plateNumber?: string;
  year?: string;
  manufacturedYear?: string;
  capacity?: number;
  fuelType?: string;
  mileage?: number;
  bootSpace?: number;
  fastTagId?: string;
  originalData?: any;
  [key: string]: any; // Allow for other properties
}

interface TripCarSectionProps {
  loading: boolean;
  tripCode: string;
  currentCarId: number | null;
  carData?: {
    id: number;
    plateNumber: string;
    manufacturedYear: string;
    fastTagId: string;
    photoId: string;
    carType: string;
  } | null;
  onUpdate: () => Promise<void>;
  disabled?: boolean;
  currentStatus: string;
  hasDriverAssigned?: boolean;
  isInProgress?: boolean;
  customerName?: string;
}

const TripCarSection: React.FC<TripCarSectionProps> = ({
  loading,
  tripCode,
  currentCarId,
  onUpdate,
  disabled = false,
  currentStatus,
  hasDriverAssigned = false,
  isInProgress,
  customerName
}) => {
  const [cars, setCars] = useState<Car[]>([]);
  const [selectedCarId, setSelectedCarId] = useState<number | null>(currentCarId || null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const carSectionRef = useRef<HTMLDivElement>(null);
  const [shouldPreserveScroll, setShouldPreserveScroll] = useState(false);
  const [dialogOpen,setDialogOpen]= useState(false);
  const [isAvailabilityChecked,setIsAvailabilityChecked]= useState(false);
  const [removeDialogOpen,setRemoveDialogOpen]= useState(false);
  const [confirmAssignDialogOpen,setConfirmAssignDialogOpen]= useState(false);

  const dispatch = useDispatch<AppDispatch>();
  const { isAvailable, affectedTrip, carPlateNumber, loading: checkingAvailability, error: availabilityError } = useSelector((state: RootState) => state.carAvailability);

  const selectedCar = cars.find(car => car.id === selectedCarId);
  // Fetch all cars when component mounts
  useEffect(() => {
    fetchCars();
  }, []);
  
   useEffect(() => {
    return () => {
      dispatch(clearAvailability());
    };
  }, [dispatch]);
  // Update selected car when currentCarId prop changes
  useEffect(() => {
    if (currentCarId) {
      setSelectedCarId(currentCarId);
    }
  }, [currentCarId]);

  // Preserve scroll position after operations complete
  useEffect(() => {
    if (shouldPreserveScroll && carSectionRef.current) {
      // Small delay to ensure DOM updates are complete
      setTimeout(() => {
        carSectionRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        setShouldPreserveScroll(false);
      }, 100);
    }
  }, [cars, selectedCarId, success, error, shouldPreserveScroll]);

  const handleRemoveCarClick = () => {
    setRemoveDialogOpen(true);
    if(currentStatus === 'CONFIRMED'){
      setRemoveDialogOpen(true);
    } else {
      handleRemoveCar();
    }
  };
  const handleRemoveCar = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setSuccess(null);
      setShouldPreserveScroll(true);
      setRemoveDialogOpen(false);
      
      // Call the remove car API
      await api.removeCarFromTrip(tripCode);
        if (currentStatus === 'CONFIRMED') {
        await api.updateTripStatus(tripCode, 'PENDING');
        setSuccess(
          'Car removed. Trip status has been reset to PENDING because no vehicle is assigned.'
        );
      } else {
        setSuccess('Car successfully removed from the trip');
      }
        // setSuccess('Car successfully removed from the trip');
        setSelectedCarId(null);
        
      // Trigger parent update to refresh trip details
      await onUpdate();
    } catch (err: any) {
      console.error('Error removing car:', err);
      setError('Car removal failed due to an invalid request. Please verify and try again.');
      setShouldPreserveScroll(false); // Reset flag on error
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCars = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Get the response from the API using getAllCars
      const response = await api.getAllCars();        // Handle the response format from the API
      let carsData: Car[] = [];
      
      if (response && response.data) {
        // Extract the cars data from the response.data array
        if (Array.isArray(response.data)) {
          carsData = response.data;
        } else if (response.data && typeof response.data === 'object') {
          // If it's a different structure with nested data
          const nestedData = response.data.data || response.data.cars || response.data.items || [];
          if (Array.isArray(nestedData)) {
            carsData = nestedData;
          } else {
            // If it's a single car object
            carsData = [response.data];
          }
        }
      } else if (Array.isArray(response)) {
        // If response is directly an array
        carsData = response;
      } else {
        console.warn('No car data received');
        carsData = [];
      }
      
       // Debug log
      setCars(carsData); // THIS WAS MISSING!
    } catch (err: any) {
      console.error('Error fetching cars:', err);
      setError('Failed to load cars. ' + (err.message || ''));
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleCarChange = (event: SelectChangeEvent<number>) => {
    const carId = event.target.value as number;
    setSelectedCarId(carId);
    dispatch(clearAvailability());
    setIsAvailabilityChecked(false);

     if (carId) {
    setDialogOpen(true);
    const selectedCarData = cars.find(car => car.id === carId);
    const carType = selectedCarData?.carType;
    const carTypeId = typeof carType === 'object' && carType?.id ? carType.id : 0;
    dispatch(checkCarAvailability({ tripCode, carTypeId, carId }));
    setIsAvailabilityChecked(true);
  }
  };
  const handleAssignCarClick = () => {
    if (!selectedCarId) {
      setError('Please select a car first');
      return;
    }

    const requiresCustomernameValidation = ['HOT LEAD', 'COLD LEAD', 'PENDING'].includes(currentStatus);
    if (requiresCustomernameValidation) {
      // Check if customer details is missing, empty, whitespace, or "NA"
      if (!customerName || customerName.trim() === '' || customerName.trim().toUpperCase() === 'N/A') {
        setError('Customer name is required to assign the car. Please add customer name first.');
        return;
      }
      const fullNameRegex = /^[a-zA-Z]+(\s+[a-zA-Z]+)+$/;
      if (!fullNameRegex.test(customerName.trim())) {
        setError('Please provide the full name of the customer (Eg: "Rajesh Kumar).');
        return;
      }
    }

    // No errors, open confirmation dialog
    setConfirmAssignDialogOpen(true);
  };
   const handleAssignCar = async () => {
    setConfirmAssignDialogOpen(false);

    try {
      setIsLoading(true);
      setError(null);
      setSuccess(null);
      setShouldPreserveScroll(true);
      
      // Call the assign car API
      await api.assignCarToTrip(tripCode, selectedCarId!);
      await api.updateTripStatus(tripCode, 'CONFIRMED');
      setSuccess('Car successfully assigned to the trip');
      
      // Update the currentCarId state after successful assignment
      setSelectedCarId(selectedCarId);
      setIsAvailabilityChecked(false);
      dispatch(clearAvailability());
      
      // Trigger parent update to refresh trip details
      await onUpdate();
    } catch (err: any) {
      console.error('Error assigning car:', err);
      setError('Failed to assign car. ' + (err.message || ''));
      setShouldPreserveScroll(false); // Reset flag on error
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <DetailSection ref={carSectionRef}>
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        Car Assignment
      </Typography>
      
      <Paper elevation={0} sx={{ 
        p: 2, 
        mb: 2, 
        bgcolor: '#1a1a1a',
        color: 'white'
      }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' }, 
          gap: 2, 
          alignItems: 'flex-end',
          bgcolor: 'transparent',
          '& .MuiInputLabel-root': {
            color: 'grey.400'
          },
          '& .MuiOutlinedInput-root': {
            color: 'white',
            '& fieldset': {
              borderColor: 'grey.700'
            },
            '&:hover fieldset': {
              borderColor: 'grey.500'
            }
          },
          '& .MuiSelect-icon': {
            color: 'grey.400'
          }
        }}>
          <FormControl fullWidth sx={{ flex: 0.7}}>
            <InputLabel id="car-select-label">Select Car</InputLabel>
            <Select
              labelId="car-select-label"
              id="car-select"
              value={selectedCarId || ''}
              label="Select Car"
              onChange={handleCarChange}
              disabled={isLoading || loading || disabled}
            >
              {Array.isArray(cars) && cars.length > 0 ? cars.map((car) => {
                const carTypeObj = typeof car.carType === 'object' ? car.carType : null;
                const makeCompany = carTypeObj?.makeCompany || '';
                const model = carTypeObj?.model || '';
                const capacity = carTypeObj?.capacity || car.capacity;
                
                return (
                  <MenuItem key={car.id} value={car.id}>
                    {makeCompany} {model} 
                    {car.plateNumber ? ` (${car.plateNumber})` : ''} 
                    {capacity ? ` • ${capacity - 1} seater` : ''}
                  </MenuItem>
                );
              }) : (
                <MenuItem value="">No cars available</MenuItem>
              )}
            </Select>
          </FormControl>
     
          <Button
            variant="contained"
            color={!isAvailable ? "error" : "success"}
            onClick={handleAssignCarClick}
            disabled={isLoading || loading || !selectedCarId || disabled}
            sx={{ minWidth: '120px',height: '50px' }}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Assign Car'}
          </Button>

          {/* Modified remove button condition */}
          {(currentCarId || selectedCarId === currentCarId) && (
            <Button
              variant="outlined"
              color="error"
              onClick={handleRemoveCarClick}
              disabled={isLoading || loading || disabled || isInProgress || currentStatus === 'DRIVER ASSIGNED'}
              sx={{ minWidth: '120px',height: '50px' }}
            >
              {isLoading ? <CircularProgress size={24} /> : 'Remove Car'}
            </Button>
          )}
          
          {/* <Button
            variant="outlined"
            onClick={fetchCars}
            disabled={isLoading || loading || disabled}
          >
            Refresh List
          </Button> */}
        </Box>
        <Box>
          
        </Box>
        <Box>
          <CarAvailabilityDialog open={dialogOpen} isAvailable={isAvailable} affectedTrip={affectedTrip} carPlateNumber={carPlateNumber} loading={loading} error={availabilityError} onClose={() => {setDialogOpen(false)}} />
        </Box>
        {selectedCarId && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="grey.400">
              {disabled ? 
                'Cannot modify car assignment - trip is cancelled' :
                currentCarId === selectedCarId ? 
                'This car is currently assigned to this trip' : 
                ""}
                {/* // 'Click "Assign Car" to assign this car to the trip' */}
            </Typography>
            {/* Display selected car details */}
            {Array.isArray(cars) && cars.length > 0 && cars.find(car => car.id === selectedCarId) && (
              <Box sx={{ 
                mt: 1, 
                p: 2, 
                bgcolor: '#2a2a2a', 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'rgba(255, 255, 255, 0.1)',
                '& .MuiTypography-root': {
                  color: 'grey.300'
                },
                '& strong': {
                  color: 'white',
                  marginRight: 1
                }
              }}>
                
                <Typography variant="body2" fontWeight="medium">
                  Selected Car Details:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={2} mt={1}>                  {(() => {
                  const selectedCar = cars.find(car => car.id === selectedCarId);
                  if (!selectedCar) return null;
                  
                  const carType = selectedCar.carType;
                  const carMake = typeof carType === 'object' ? carType.makeCompany : '';
                  const carModel = typeof carType === 'object' ? carType.model : '';
                  const carCapacity = typeof carType === 'object' ? carType.capacity : selectedCar.capacity;
                  const carFuelType = typeof carType === 'object' ? carType.fuelType : selectedCar.fuelType;
                  
                  return (
                    <>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                        <strong>Make & Model:</strong> {carMake} {carModel}
                      </Typography>
                      {selectedCar.plateNumber && (
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                          <strong>Plate Number:</strong> {selectedCar.plateNumber}
                        </Typography>
                      )}
                      {carCapacity && (
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                          <strong>Capacity:</strong> {carCapacity - 1} seater
                        </Typography>
                      )}
                      {carFuelType && (
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                          <strong>Fuel Type:</strong> {carFuelType}
                        </Typography>
                      )}
                      {selectedCar.manufacturedYear && (
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                          <strong>Year:</strong> {selectedCar.manufacturedYear}
                        </Typography>
                      )}
                      {selectedCar.fastTagId && (
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                          <strong>Fast Tag ID:</strong> {selectedCar.fastTagId} 
                         </Typography>
                      )}
                      </>
                   );
                })()}
                 </Box>
                </Box>
            )}
          </Box>
        )}
      </Paper>
      <Dialog
        open={removeDialogOpen}
        onClose = {() => setRemoveDialogOpen(false)}
        aria-labelledby="remove-car-confirmation"
        PaperProps={{
          sx: {
            backgroundColor: 'white',
            padding: 2
          }
        }}
      >
        <DialogTitle id="remove-car-confirmation">Confirm Car Removal</DialogTitle>
        <DialogContent>
          <DialogContentText color="error">Removing the car will change the trip status to "PENDING". <br/> Are you sure you want to proceed?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button  variant="contained" onClick={() => setRemoveDialogOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleRemoveCar}>Continue</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmAssignDialogOpen}
        onClose = {() => setConfirmAssignDialogOpen(false)}
        aria-labelledby="assign-car-confirmation"
        PaperProps={{
          sx: {
            backgroundColor: 'white',
            padding: 2
          }
        }}
      >
        <DialogTitle id="assign-car-confirmation">Confirm Car Assignment</DialogTitle>
        <DialogContent>
          <DialogContentText color="error">Assigning car will change the Trip status to Confirm.<br/> Are you sure you want to proceed with car assignment?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button  variant="contained" onClick={() => setConfirmAssignDialogOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleAssignCar}>Continue</Button>
        </DialogActions>
      </Dialog>
    </DetailSection>
  );
};

export default TripCarSection;
